import React, { useState, useEffect } from "react";
import settingsService from "../../../Modules/Services/settings.service";
import {
  formatRuPhoneInput,
  normalizeRuPhoneE164,
} from "../../../Modules/Utils/phoneRu";
import "./ManageSettings.css";

const ManageSettings = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  /** Нормализованное значение с сервера для сравнения */
  const [phoneNormInitial, setPhoneNormInitial] = useState("");
  const [orderAccepting, setOrderAccepting] = useState(true);
  const [initialOrderAccepting, setInitialOrderAccepting] = useState(true);
  const [disabledReason, setDisabledReason] = useState("");
  const [initialDisabledReason, setInitialDisabledReason] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const reasonOptions = [
    "Закрыты",
    "Технические работы",
    "Большая нагрузка",
    "Перерыв",
    "Кухня перегружена",
  ];

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsLoading(true);
        const [phoneData, orderData] = await Promise.all([
          settingsService.getPhoneNumber().catch(() => ({ phoneNumber: "" })),
          settingsService.getOrderStatus().catch(() => ({
            accepting: true,
            reason: null,
          })),
        ]);

        const rawPhone = phoneData.phoneNumber || "";
        setPhoneNumber(formatRuPhoneInput(rawPhone));
        setPhoneNormInitial(
          normalizeRuPhoneE164(rawPhone) || rawPhone.replace(/\D/g, "").trim()
        );

        setOrderAccepting(Boolean(orderData.accepting));
        setInitialOrderAccepting(Boolean(orderData.accepting));
        setDisabledReason(orderData.reason || "");
        setInitialDisabledReason(orderData.reason || "");
        setError(null);
      } catch (err) {
        setError("Не удалось загрузить настройки.");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      setSuccessMessage("");
      setError(null);
      const normNow =
        normalizeRuPhoneE164(phoneNumber) ||
        phoneNumber.replace(/\D/g, "").trim();

      if (normNow !== phoneNormInitial) {
        await settingsService.updatePhoneNumber(normNow);
        setPhoneNormInitial(normNow);
        setPhoneNumber(formatRuPhoneInput(normNow));
      }

      const effectiveReason = orderAccepting ? "" : disabledReason;
      const orderChanged =
        orderAccepting !== initialOrderAccepting ||
        effectiveReason !== initialDisabledReason;
      if (orderChanged) {
        await settingsService.updateOrderStatus({
          accepting: orderAccepting,
          reason: effectiveReason,
        });
        setInitialOrderAccepting(orderAccepting);
        setInitialDisabledReason(effectiveReason);
      }

      setSuccessMessage("Сохранено.");
    } catch (err) {
      setError("Не удалось сохранить.");
      console.error(err);
    }
  };

  const effectiveReason = orderAccepting ? "" : disabledReason;
  const normPhoneEdited =
    normalizeRuPhoneE164(phoneNumber) ||
    phoneNumber.replace(/\D/g, "").trim();

  const isChanged =
    normPhoneEdited !== phoneNormInitial ||
    orderAccepting !== initialOrderAccepting ||
    effectiveReason !== initialDisabledReason;

  if (isLoading) {
    return <div className="admin-spinner">Загрузка…</div>;
  }

  return (
    <div className="ManageSettings surface">
      <h1 className="admin-page-title">Настройки</h1>

      <form onSubmit={handleUpdate} className="ManageSettings-form">
        <div className="admin-field">
          <label className="admin-field__label" htmlFor="settings-phone">
            Телефон для WhatsApp
          </label>
          <input
            id="settings-phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+7 (900) 000-00-00"
            maxLength={18}
            className="admin-input"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(formatRuPhoneInput(e.target.value))}
          />
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Приём заказов</span>
          <div className="admin-toggle-panel">
            <label className="admin-toggle">
              <input
                type="checkbox"
                checked={orderAccepting}
                onChange={(e) => setOrderAccepting(e.target.checked)}
              />
              <span className="admin-toggle__track" aria-hidden>
                <span className="admin-toggle__thumb" />
              </span>
            </label>
            <div className="admin-toggle-panel__text">
              <span
                className={`admin-toggle-panel__state ${orderAccepting ? "is-on" : "is-off"}`}
              >
                {orderAccepting ? "Сайт принимает заказы" : "Приём заказов выключен"}
              </span>
              <span className="admin-field__hint">
                Отключите на время перерыва или при высокой загрузке кухни.
              </span>
            </div>
          </div>
        </div>

        {!orderAccepting && (
          <div className="admin-field">
            <label className="admin-field__label" htmlFor="settings-reason">
              Причина отключения
            </label>
            <div className="admin-select-wrap">
              <select
                id="settings-reason"
                className="admin-select admin-select--styled"
                value={disabledReason}
                onChange={(e) => setDisabledReason(e.target.value)}
              >
                <option value="">Выберите причину…</option>
                {reasonOptions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {error && (
          <div className="admin-flash admin-flash--error" role="alert">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="admin-flash admin-flash--success" role="status">
            {successMessage}
          </div>
        )}

        <button
          type="submit"
          className="btn btn--primary"
          disabled={!isChanged}
        >
          Сохранить
        </button>
      </form>
    </div>
  );
};

export default ManageSettings;
